import React from 'react'

const EventCard = ({ event }) => {
  // Get the primary image URL
  const getImageUrl = () => {
    if (event.image_urls?.standard) {
      return event.image_urls.standard
    }
    if (event.image_urls?.hd) {
      return event.image_urls.hd
    }
    return null
  }

  // Format date range for display
  const getDateRange = () => {
    if (!event.instances || event.instances.length === 0) return null
    
    const sortedInstances = [...event.instances].sort((a, b) => 
      new Date(a.start_time) - new Date(b.start_time)
    )
    
    const firstDate = new Date(sortedInstances[0].start_time)
    const lastDate = new Date(sortedInstances[sortedInstances.length - 1].start_time)
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      })
    }
    
    if (sortedInstances.length === 1) {
      return formatDate(firstDate)
    }
    
    // Same day, multiple times
    if (firstDate.toDateString() === lastDate.toDateString()) {
      return formatDate(firstDate)
    }
    
    // Different days
    return `${formatDate(firstDate)} - ${formatDate(lastDate)}`
  }

  // Get lowest price from all instances
  const getLowestPrice = () => {
    if (!event.instances || event.instances.length === 0) return null
    
    const prices = event.instances
      .map(i => i.lowest_price)
      .filter(p => p !== null && p !== undefined)
    
    if (prices.length === 0) return null
    
    const lowest = Math.min(...prices)
    return lowest
  }

  // Check if any instances have availability
  const hasAvailability = () => {
    return event.instances?.some(i => i.available_tickets > 0)
  }
  
  // Check if all instances are free of charge
  const isFreeOfCharge = () => {
    return event.instances?.every(i => i.free_of_charge === true)
  }
  
  // Check if all instances are not on sale or locked
  const needsPhoneBooking = () => {
    return event.instances?.every(i => !i.on_sale || i.locked)
  }
  
  // Check if any instances have limited availability
  const hasLimitedAvailability = () => {
    return event.instances?.some(i => i.limited_availability === true) && hasAvailability()
  }

  const isSoldOut = !hasAvailability() && !isFreeOfCharge() && !needsPhoneBooking()
  const imageUrl = getImageUrl()
  const dateRange = getDateRange()
  const lowestPrice = getLowestPrice()

  return (
    <a
      href={`/events/${event.slug}`}
      className="block group bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative aspect-[4/3]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={event.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <svg
              className="w-16 h-16 text-gray-400 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
        
        {/* Certificate overlay */}
        {event.certificate_image_url && (
          <div className="absolute top-2 right-2">
            <img 
              src={event.certificate_image_url} 
              alt={`${event.certificate_name || 'Certificate'}`}
              className="h-8 w-auto"
            />
          </div>
        )}
        
        {/* Offers overlay */}
        {event.offers && event.offers.length > 0 && (
          <div className="absolute top-2 left-2 space-y-1">
            {event.offers.slice(0, 2).map((offer, index) => (
              <div
                key={offer.id || index}
                className="bg-gray-800 dark:bg-gray-900 text-white text-xs font-bold px-2 py-1 rounded shadow-lg flex items-center"
              >
                <svg className="w-3 h-3 text-amber-400 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span dangerouslySetInnerHTML={{ __html: offer.description || offer.htmlDescription || offer.name }} />
              </div>
            ))}
            {event.offers.length > 2 && (
              <div className="bg-gray-800 dark:bg-gray-900 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
                +{event.offers.length - 2} more
              </div>
            )}
          </div>
        )}
        
        {/* Status overlay */}
        {isFreeOfCharge() ? (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-green-600 text-white text-lg font-bold px-4 py-2 rounded transform rotate-12">
              FREE
            </div>
          </div>
        ) : isSoldOut && !needsPhoneBooking() ? (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-red-600 text-white text-lg font-bold px-4 py-2 rounded transform rotate-12">
              SOLD OUT
            </div>
          </div>
        ) : hasLimitedAvailability() ? (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-amber-600 text-white text-lg font-bold px-4 py-2 rounded transform rotate-12">
              LIMITED AVAILABILITY
            </div>
          </div>
        ) : null}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        {/* Categories */}
        {event.category_list && event.category_list.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {event.category_list.map((category, index) => (
              <span
                key={index}
                className={`text-xs px-2 py-0.5 rounded text-white ${event.category_colors?.[index] || 'bg-gray-500'}`}
              >
                {category.toLowerCase()}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="font-serif text-base sm:text-lg text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 mb-2 line-clamp-2">
          {event.name}
        </h3>

        {/* Date and Price */}
        <div className="flex items-center justify-between text-sm">
          {dateRange && (
            <span className="text-gray-600 dark:text-gray-400">
              {dateRange}
            </span>
          )}
          
          {isFreeOfCharge() ? (
            <span className="text-green-600 dark:text-green-400 font-medium">
              Free
            </span>
          ) : lowestPrice !== null && !isSoldOut ? (
            <span className="text-gray-900 dark:text-white font-medium">
              From £{lowestPrice.toFixed(lowestPrice % 1 === 0 ? 0 : 2)}
            </span>
          ) : null}
        </div>

        {/* Instance count */}
        {event.instances && event.instances.length > 1 && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            {event.instances.length} performances
          </div>
        )}
      </div>
    </a>
  )
}

export default EventCard